import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2, MessageSquareHeart, Pencil, Plus, Trash2 } from "lucide-react";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import {
  adminWelcomeMessagesApi,
  type AdminWelcomeMessage,
  type AdminWelcomeMessageInput,
  type WelcomeGymOption,
} from "@/services/edgeFunctionWrappers/adminWelcomeMessagesWrapper";

const QK = ["admin-welcome-messages"];
const EMPTY: AdminWelcomeMessageInput = { title: "", body: "", gym_ids: [], is_active: true };

export default function SuperAdminWelcomeMessages() {
  const { t } = useTranslation("welcome");
  const qc = useQueryClient();
  const [form, setForm] = useState<AdminWelcomeMessageInput | null>(null);
  const [toDelete, setToDelete] = useState<AdminWelcomeMessage | null>(null);

  const { data, isLoading, error, refetch } = useQuery({ queryKey: QK, queryFn: adminWelcomeMessagesApi.list });
  const gyms: WelcomeGymOption[] = data?.gyms ?? [];
  const gymName = (id: string) => gyms.find((g) => g.id === id)?.name ?? id.slice(0, 8);

  const save = useMutation({
    mutationFn: adminWelcomeMessagesApi.upsert,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      setForm(null);
      toast({ title: t("admin.saved") });
    },
    onError: () => toast({ title: t("admin.error"), variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: adminWelcomeMessagesApi.remove,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      setToDelete(null);
      toast({ title: t("admin.deleted") });
    },
    onError: () => toast({ title: t("admin.error"), variant: "destructive" }),
  });

  const toggleGym = (id: string, checked: boolean) =>
    form && setForm({ ...form, gym_ids: checked ? [...form.gym_ids, id] : form.gym_ids.filter((g) => g !== id) });

  const valid = !!form && form.title.trim() && form.body.trim() && form.gym_ids.length > 0;

  return (
    <SuperAdminLayout>
      <div className="space-y-4 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <MessageSquareHeart className="h-6 w-6" /> {t("admin.title")}
            </h1>
            <p className="text-sm text-muted-foreground">{t("admin.description")}</p>
          </div>
          <Button onClick={() => setForm({ ...EMPTY })} className="min-h-[44px]">
            <Plus className="h-4 w-4 mr-2" /> {t("admin.new")}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : error ? (
          <Card><CardContent className="py-8 text-center space-y-3">
            <p>{t("admin.loadError")}</p>
            <Button variant="outline" onClick={() => refetch()}>{t("admin.retry")}</Button>
          </CardContent></Card>
        ) : (data?.messages ?? []).length === 0 ? (
          <Card><CardContent className="py-10 text-center text-muted-foreground">{t("admin.empty")}</CardContent></Card>
        ) : (
          <div className="grid gap-3">
            {data!.messages.map((m) => (
              <Card key={m.id}>
                <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2 space-y-0">
                  <div className="min-w-0">
                    <CardTitle className="text-lg break-words">{m.title}</CardTitle>
                    <Badge variant={m.is_active ? "default" : "secondary"} className="mt-1">
                      {m.is_active ? t("admin.active") : t("admin.inactive")}
                    </Badge>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="icon" variant="ghost" aria-label={t("admin.edit")}
                      onClick={() => setForm({ id: m.id, title: m.title, body: m.body, gym_ids: m.gym_ids, is_active: m.is_active })}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" aria-label={t("admin.delete")} onClick={() => setToDelete(m)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-sm text-muted-foreground whitespace-pre-line line-clamp-3">{m.body}</p>
                  <div className="flex flex-wrap gap-1">
                    {m.gym_ids.map((g) => <Badge key={g} variant="outline">{gymName(g)}</Badge>)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form?.id ? t("admin.edit") : t("admin.new")}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="wm-title">{t("admin.fieldTitle")}</Label>
                <Input id="wm-title" maxLength={150} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="wm-body">{t("admin.fieldBody")}</Label>
                <Textarea id="wm-body" rows={6} maxLength={5000} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{t("admin.fieldGyms")}</Label>
                  <Button type="button" variant="link" size="sm" className="h-auto p-0"
                    onClick={() => setForm({ ...form, gym_ids: form.gym_ids.length === gyms.length ? [] : gyms.map((g) => g.id) })}>
                    {form.gym_ids.length === gyms.length ? t("admin.selectNone") : t("admin.selectAll")}
                  </Button>
                </div>
                <div className="max-h-48 overflow-y-auto rounded-md border p-2 space-y-1">
                  {gyms.map((g) => (
                    <label key={g.id} className="flex items-center gap-2 py-1.5 cursor-pointer min-h-[36px]">
                      <Checkbox checked={form.gym_ids.includes(g.id)} onCheckedChange={(c) => toggleGym(g.id, c === true)} />
                      <span className="text-sm">{g.name}{g.city ? ` — ${g.city}` : ""}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="wm-active">{t("admin.fieldActive")}</Label>
                <Switch id="wm-active" checked={form.is_active} onCheckedChange={(c) => setForm({ ...form, is_active: c })} />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setForm(null)}>{t("admin.cancel")}</Button>
            <Button disabled={!valid || save.isPending} onClick={() => form && save.mutate(form)}>
              {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{t("admin.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("admin.deleteDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("admin.cancel")}</AlertDialogCancel>
            <AlertDialogAction disabled={remove.isPending} onClick={() => toDelete && remove.mutate(toDelete.id)}>
              {t("admin.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SuperAdminLayout>
  );
}
